import React, { useRef, useState } from "react";
import { api, extractApiError } from "@/lib/api";
import { toast } from "sonner";
import { Mail } from "lucide-react";
import { useT, useLanguage } from "@/lib/LanguageContext";
import { dateLocale } from "@/lib/i18n";
// Relative on purpose: no "@" alias on the Jest side (cf. LanguageAwareField.jsx).
import usePolling from "../../hooks/usePolling";
import { payloadShapeError } from "../../hooks/useAdminLoad";
import LoadFailure from "../../components/ui/LoadFailure";
import { POLL_INTERVAL_MS } from "../ConversationsContext";
import Composer from "./Composer";

/**
 * One conversation, read and replied to.
 *
 * THE THREAD IS A DELTA, NOT A REFETCH. The first read asks for the newest page; every
 * poll after it asks `after=<the highest seq already on screen>`, so a long thread does
 * not re-ship itself every minute and the merge has nothing to reconcile. `seq` is a
 * per-conversation integer allocated by the server in the same atomic write that stores
 * the message (modules/messaging/repository.py), which is why it is the cursor and
 * `created_at` is not: two messages can share a timestamp string, and none can share a
 * seq.
 *
 * MARKING READ IS AN ASSERTION ABOUT WHAT IS ON SCREEN, so it carries the highest seq
 * RENDERED, and it is sent exactly once per new high-water mark — on open, and again each
 * time a poll brings something in while the thread is open. `markedRef` is claimed BEFORE
 * the request so a poll landing mid-flight cannot send a second PUT for the same seq, and
 * released again if the request fails, so the next poll retries. The server's marker is
 * `$max`, so a late or repeated call can never move it backwards.
 *
 * The answering `ConversationOut` is handed straight to `onPatch`: the sidebar pill and
 * the list's dot clear on the reply the server just gave, not on a guess, and not a minute
 * later when the next list poll happens to run.
 *
 * Mounted with `key={conversationId}` by MessagesAdmin, so switching conversations
 * remounts it: no stale message can survive into another client's thread.
 */
const dtFmt = (iso, lang) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(dateLocale(lang), { dateStyle: "medium", timeStyle: "short" });
};

/** Merge by id, ascending by seq — the shape every read of this thread produces. */
const mergeMessages = (prev, incoming) => {
  const byId = new Map(prev.map((m) => [m.id, m]));
  for (const m of incoming) {
    if (m && m.id) byId.set(m.id, m);
  }
  return [...byId.values()].sort((a, b) => a.seq - b.seq);
};

export default function Thread({ conversationId, summary, onPatch, onSent }) {
  const t = useT("admin");
  const tc = useT("common");
  const { lang } = useLanguage();
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [olderBusy, setOlderBusy] = useState(false);
  // The highest seq held (null until the first read answers), and the highest seq already
  // claimed by a PUT …/read.
  const highestRef = useRef(null);
  const markedRef = useRef(0);

  const markRead = async (seq) => {
    if (!seq || seq <= markedRef.current) return;
    const previous = markedRef.current;
    markedRef.current = seq;
    try {
      const { data } = await api.put(`/admin/conversations/${conversationId}/read`, {
        last_seen_seq: seq,
      });
      onPatch(data);
    } catch {
      // Silent by design: the message IS on screen, only the marker failed. Releasing the
      // claim lets the next poll try again; a toast here would report a failure the
      // practitioner cannot act on, over and over, once a minute.
      markedRef.current = previous;
    }
  };

  const sync = async () => {
    const after = highestRef.current;
    try {
      const { data } = await api.get(
        `/admin/conversations/${conversationId}`,
        after != null ? { params: { after } } : undefined,
      );
      if (!data || !Array.isArray(data.messages)) throw payloadShapeError();
      setLoadError(null);
      if (data.conversation) setConversation(data.conversation);
      if (data.messages.length) {
        setMessages((prev) => mergeMessages(prev, data.messages));
        highestRef.current = data.messages.reduce((hi, m) => Math.max(hi, m.seq), after || 0);
      } else if (after == null) {
        // An empty thread still has to move off "no cursor yet", or every poll would ask
        // for the whole history again.
        highestRef.current = 0;
      }
      // Start the high-water mark at the server's own marker, so a thread that was
      // already read writes nothing at all.
      const readSeq = data.conversation?.practitioner_read_seq || 0;
      if (readSeq > markedRef.current) markedRef.current = readSeq;
      await markRead(highestRef.current);
    } catch (e) {
      setLoadError(e || new Error());
    }
  };

  const { refresh } = usePolling(sync, { intervalMs: POLL_INTERVAL_MS });

  const oldestSeq = messages.length ? messages[0].seq : null;
  // seq is 1-based and gap-free, so "the oldest I hold is not the first" is an exact
  // statement that older messages exist — no guessing from a page size.
  const hasOlder = oldestSeq != null && oldestSeq > 1;

  const loadOlder = async () => {
    if (!hasOlder || olderBusy) return;
    setOlderBusy(true);
    try {
      const { data } = await api.get(`/admin/conversations/${conversationId}`, {
        params: { before: oldestSeq },
      });
      if (!data || !Array.isArray(data.messages)) throw payloadShapeError();
      setMessages((prev) => mergeMessages(prev, data.messages));
    } catch (e) {
      // Unlike the poll's failure, this one answers a click: it is reported where the
      // click was, and the thread already on screen stays.
      toast.error(extractApiError(e, tc("errors.loadFailed"), tc));
    } finally {
      setOlderBusy(false);
    }
  };

  const header = conversation || summary;

  return (
    <div className="flex flex-col h-full" data-testid="admin-thread">
      {header && (
        <div className="mb-4">
          <div className="font-display text-2xl text-eh-ink">
            {header.client_name || header.client_email}
          </div>
          <a
            href={`mailto:${header.client_email}`}
            className="text-sm text-eh-sage-deep hover:underline inline-flex items-center gap-1"
          >
            <Mail size={14} /> {header.client_email}
          </a>
        </div>
      )}

      <LoadFailure
        message={loadError ? extractApiError(loadError, tc("errors.loadFailed"), tc) : ""}
        onRetry={refresh}
        retryLabel={tc("actions.retry")}
        testId="admin-thread-load-error"
        retryTestId="admin-thread-retry"
      />

      <div className="flex-1 space-y-3" data-testid="admin-thread-messages">
        {hasOlder && (
          <button
            type="button"
            onClick={loadOlder}
            disabled={olderBusy}
            data-testid="admin-thread-load-older"
            className="text-xs underline text-eh-muted"
          >
            {t("msg.loadOlder")}
          </button>
        )}
        {!loadError && messages.length === 0 && (
          <div className="text-sm text-eh-muted">{t("msg.noMessagesYet")}</div>
        )}
        {messages.map((m) => {
          const mine = m.author_kind === "practitioner";
          return (
            <div
              key={m.id}
              data-testid={`admin-message-${m.id}`}
              className={`flex ${mine ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-[85%] rounded-2xl px-4 py-3 ${mine ? "bg-eh-sage/20" : "bg-eh-bg"}`}
              >
                <div className="text-[10px] uppercase tracking-wider text-eh-muted mb-1">
                  {mine ? t("msg.you") : t("msg.client")} · {dtFmt(m.created_at, lang)}
                </div>
                <div className="whitespace-pre-wrap text-eh-ink leading-relaxed text-sm">{m.body}</div>
              </div>
            </div>
          );
        })}
      </div>

      <Composer
        conversationId={conversationId}
        onSent={(message) => {
          if (!message || !message.id) return;
          setMessages((prev) => mergeMessages(prev, [message]));
          // The reply is the new high-water mark, and it is READ by definition — its
          // author is reading it. Claiming it here stops the next poll from PUTting a
          // marker for a message the practitioner wrote themselves.
          highestRef.current = Math.max(highestRef.current || 0, message.seq);
          markedRef.current = Math.max(markedRef.current, message.seq);
          // The LIST is now stale in two ways this pane cannot fix — the preview and the
          // ordering both changed — so the shared poll is forced instead of waiting out
          // its minute.
          onSent(message);
        }}
      />
    </div>
  );
}
